import { TURN_INTO_OPTIONS } from "@/components/tailwind/block-context-menu/TurnIntoMenu";
import {
  copyLinkToBlock,
  duplicateBlock,
  moveBlock,
  resolveBlockTarget,
  toggleBlockSelection,
} from "@/components/tailwind/block-context-menu/block-actions";
import { emitFindInPage } from "../events";
import { toggleOpenAtSelection } from "@/lib/toggle-state-store";
import type { Binding, ShortcutContext } from "../types";

/**
 * Number-key block conversions, matching the layout people arriving from other
 * block editors already have in their fingers.
 *
 * The two platforms genuinely differ here: ⌘⌥N on Apple, Ctrl+Shift+N
 * elsewhere. Ctrl+Alt is avoided on Windows and Linux because it is AltGr on
 * many international layouts, where binding it would eat characters people
 * need to type.
 *
 * 9 (new page) is deliberately absent: the "page" entry in TURN_INTO_OPTIONS
 * is a stub whose command only calls clearNodes() — binding it would strip the
 * block's formatting and create nothing. It can be added the moment that
 * option does something real.
 */
const TURN_INTO_KEYS: Array<{ digit: string; optionId: string; label: string }> = [
  { digit: "0", optionId: "text", label: "Turn into text" },
  { digit: "1", optionId: "heading1", label: "Turn into heading 1" },
  { digit: "2", optionId: "heading2", label: "Turn into heading 2" },
  { digit: "3", optionId: "heading3", label: "Turn into heading 3" },
  { digit: "7", optionId: "toggleList", label: "Turn into toggle list" },
  // 4 (to-do) and 5 (bulleted) are withheld pending an unresolved bug: in the
  // running app they leave the text unconverted, though the same conversion
  // produces a correct taskList/bulletList document when driven headlessly
  // against the real extension set. Whatever the cause, it is not worth
  // shipping two keys that visibly do nothing. Restore both rows once fixed.
  // { digit: "4", optionId: "todoList", label: "Turn into to-do list" },
  // { digit: "5", optionId: "bulletList", label: "Turn into bulleted list" },
  { digit: "6", optionId: "numberedList", label: "Turn into numbered list" },
  { digit: "8", optionId: "code", label: "Turn into code block" },
];

/**
 * Convert whatever the selection currently covers.
 *
 * Runs the option's `.chain` — the raw transformation against the live
 * selection — which is the same path the bubble menu's node selector takes.
 * The alternative, calling the command with an explicit block position, is
 * built for the drag handle and needs the *list item* rather than the
 * paragraph inside it; handed a paragraph it selects a range instead of
 * placing a cursor, and list-to-list conversions then silently do nothing.
 *
 * Going through the selection also means a multi-block selection converts
 * every block in it, which is what someone selecting three lines expects.
 */
function runTurnInto(optionId: string, { editor }: ShortcutContext): boolean | void {
  if (!editor) return false;

  const option = TURN_INTO_OPTIONS.find((o) => o.id === optionId);
  if (!option) return false;

  const apply = (option.command as { chain?: (chain: unknown, editor: unknown) => { run: () => void } })
    .chain;

  if (typeof apply === "function") {
    apply(editor.chain().focus(), editor).run();
    return;
  }

  // Options built outside createTurnIntoCommand carry no `.chain` (the column
  // layouts). None are bound today; fall back rather than silently no-op.
  const target = resolveBlockTarget(editor);
  if (!target) return false;
  option.command(editor, target.node, target.position);
}

const turnIntoBindings: Binding[] = TURN_INTO_KEYS.map(({ digit, optionId, label }) => ({
  id: `editor.turnInto.${optionId}`,
  keys: { apple: `Mod+Alt+${digit}`, other: `Mod+Shift+${digit}` },
  scope: "editor",
  label,
  group: "Turn into",
  requiresEditable: true,
  run: (ctx) => runTurnInto(optionId, ctx),
}));

export const editorBindings: Binding[] = [
  ...turnIntoBindings,
  {
    id: "editor.toggleExpand",
    keys: "Mod+Enter",
    scope: "editor",
    label: "Expand or collapse toggle",
    group: "Blocks",
    // Per-user view state, not an edit — works in read-only editors too.
    run: ({ editor }) => {
      if (!editor) return false;
      return toggleOpenAtSelection(editor) ? undefined : false;
    },
  },
  {
    id: "editor.duplicate",
    keys: "Mod+D",
    scope: "editor",
    label: "Duplicate block",
    group: "Blocks",
    requiresEditable: true,
    run: ({ editor }) => {
      if (!editor) return false;
      const target = resolveBlockTarget(editor);
      if (!target) return false;
      duplicateBlock(editor, target.node, target.position);
    },
  },
  {
    id: "editor.copyLink",
    keys: "Mod+L",
    scope: "editor",
    label: "Copy link to block",
    group: "Blocks",
    run: ({ editor }) => {
      if (!editor) return false;
      const target = resolveBlockTarget(editor);
      if (!target) return false;
      // Fire-and-forget: the clipboard write is async but nothing downstream
      // depends on it, and returning a promise would read as "handled" anyway.
      void copyLinkToBlock(target.node);
    },
  },
  {
    id: "editor.toggleBlockSelection",
    keys: "Escape",
    scope: "editor",
    label: "Select block, or return to editing",
    group: "Selection",
    // Menus and dialogs sit in narrower scopes and are consulted first, so
    // Escape still closes the slash menu or a dialog before it reaches here.
    run: ({ editor }) => {
      if (!editor) return false;
      return toggleBlockSelection(editor);
    },
  },
  {
    id: "editor.moveBlockUp",
    keys: "Mod+Shift+Up",
    scope: "editor",
    label: "Move block up",
    group: "Blocks",
    requiresEditable: true,
    run: ({ editor }) => (editor ? moveBlock(editor, "up") : false),
  },
  {
    id: "editor.moveBlockDown",
    keys: "Mod+Shift+Down",
    scope: "editor",
    label: "Move block down",
    group: "Blocks",
    requiresEditable: true,
    run: ({ editor }) => (editor ? moveBlock(editor, "down") : false),
  },
  {
    id: "editor.findInPage",
    keys: "Mod+F",
    // Global rather than editor scope so it still works after clicking away
    // from the text — but it carries the focused editor, so with a peek panel
    // open the right one responds.
    scope: "global",
    label: "Find on page",
    group: "Navigation",
    allowInInput: true,
    run: ({ editor }) => {
      // ctx.editor is already the focused editor, or null when focus is
      // outside every one — which the main page's bar treats as its own.
      emitFindInPage(editor);
    },
  },
];
